import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, CheckCircle2, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { F350Datos } from './F350Datos';
import { F350Conceptos } from './F350Conceptos';
import { F350DetalleDialog } from './F350DetalleDialog';
import { F350Exterior } from './F350Exterior';
import { F350Sanciones } from './F350Sanciones';
import { fmtMoney } from './f350Utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Formulario350 = ({ declId, decl }) => {
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState(null);
  const [computed, setComputed] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [detalle, setDetalle] = useState({ open: false, concepto: null, tipo: 'pj' });
  const [pdfLoading, setPdfLoading] = useState(false);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/f350`).then(({ data: r }) => { setSchema(r.schema); setData(r.data); setComputed(r.computed); })
      .catch((e) => toast.error(e.response?.data?.detail || 'No se pudo cargar el formulario'));
  }, [declId]);

  const save = useCallback(async (d) => {
    setSaveState('saving');
    try {
      const { data: r } = await axios.put(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/f350`, { data: d });
      setComputed(r.computed); setSaveState('saved'); dirty.current = false;
    } catch (e) { setSaveState('error'); toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  }, [declId]);

  const update = (patch) => {
    setData((prev) => {
      const next = { ...prev, ...patch };
      dirty.current = true;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => save(next), 700);
      return next;
    });
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const descargarPdf = async () => {
    setPdfLoading(true);
    try {
      if (dirty.current) { clearTimeout(timer.current); await save(data); }
      const res = await axios.get(`${BACKEND_URL}/api/tributario/declaraciones/${declId}/f350/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `F350_${data.datos.nit || decl.nit}_${data.datos.anio}_${data.datos.periodo || ''}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error('No se pudo generar el PDF'); }
    finally { setPdfLoading(false); }
  };

  if (!schema || !data) return <div className="p-6 text-sm text-slate-500">Cargando formulario…</div>;
  const cas = computed?.casillas || {};
  return (
    <div className="space-y-5" data-testid="f350-form">
      <div className="sticky top-[92px] z-10 -mx-1 px-1 py-2 bg-[#F6F7F9]/95 backdrop-blur flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {[[130, 'Renta'], [134, 'IVA'], [136, 'Total retenciones'], [137, 'Sanciones'], [980, 'Pago total']].map(([n, l]) => (
            <div key={n} className={`rounded-xl border px-3 py-1.5 ${n === 980 ? 'border-[#1F3864] bg-[#1F3864] text-white' : 'border-slate-200 bg-white'}`} data-testid={`f350-resumen-${n}`}>
              <div className={`text-[10px] uppercase tracking-wide ${n === 980 ? 'text-blue-100' : 'text-slate-500'}`}>{n} · {l}</div>
              <div className="text-sm font-semibold tabular-nums">{fmtMoney(cas[n])}</div>
            </div>
          ))}
        </div>
        <span className="flex-1" />
        <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="f350-save-state">
          {saveState === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</>}
          {saveState === 'saved' && <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Guardado</>}
          {saveState === 'error' && <span className="text-red-600">Error al guardar</span>}
          {saveState === 'idle' && <><CloudUpload className="w-3.5 h-3.5" /> Se guarda automáticamente</>}
        </span>
        <Button onClick={descargarPdf} disabled={pdfLoading} className="bg-emerald-700 hover:bg-emerald-800" data-testid="f350-pdf-btn">
          {pdfLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generando…</> : <><FileText className="w-4 h-4 mr-1" /> PDF Formulario 350 + anexos</>}
        </Button>
      </div>

      <F350Datos datos={data.datos || {}} periodos={schema.periodos} onChange={(datos) => update({ datos })} />
      <F350Conceptos schema={schema} data={data} computed={computed}
        onCasilla={(num, v) => update({ casillas: { ...(data.casillas || {}), [num]: v } })}
        onDetalle={(concepto, tipo) => setDetalle({ open: true, concepto, tipo })} />
      <F350Exterior lines={data.exterior || []} onChange={(exterior) => update({ exterior })} />
      <F350Sanciones sanciones={data.sanciones || {}} computed={computed?.sanciones} onChange={(sanciones) => update({ sanciones })} />

      <F350DetalleDialog open={detalle.open} onOpenChange={(o) => setDetalle((d) => ({ ...d, open: o }))} concepto={detalle.concepto} tipo={detalle.tipo}
        lines={detalle.concepto ? data.detalles?.[`${detalle.concepto.key}_${detalle.tipo}`] : []}
        onSave={(lines) => { const key = `${detalle.concepto.key}_${detalle.tipo}`; const detalles = { ...(data.detalles || {}) }; if (lines.length) detalles[key] = lines; else delete detalles[key]; update({ detalles }); }} />
    </div>
  );
};
